import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import Dashboard from "@uppy/dashboard"
import ImageEditor from "@uppy/image-editor"
import Tus from "@uppy/tus"
import Russian from '@uppy/locales/lib/ru_RU'
import '@uppy/image-editor/dist/style.css'
export default class extends Controller {
  static targets = ["modal", "title", "loading", "imageContainer", "image", "pdfContainer", "pdf", "docContainer", "docName", "docLink", "startButton", "closeButton"]
  static values = {
    url: String,
    fileType: String,
  }

  // Instance properties
  fileType = ""
  fileName = ""

  connect() {
    // Initialize modal when controller connects using the target element
    this.modal = new bootstrap.Modal(this.modalTarget, {
      keyboard: true,
      focus: true
    })
  }

  show(event) {
    // Store the element that triggered the modal to return focus later
    this.lastActiveElement = document.activeElement

    // Show loading indicator
    this.showLoading(true)

    // Reset all content containers
    this.resetContainers()

    const targetElement = event.currentTarget || event.target

    const url = targetElement.dataset.url
    this.fileType = targetElement.dataset.fileType || ""
    this.fileName = event.currentTarget.dataset.fileName || ""
    if (url) {
      if (this.fileType === 'image') {
        this.showImage(url)
      } else if (this.fileType === '.pdf') {
        this.showPdf(url)
      } else {
        this.showDoc(url)
      }
    }

    // Show the modal
    this.modal.show()

    // Set focus on close button after modal is shown
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.closeButtonTarget.focus()
    }, { once: true })
  }

  // Helper methods
  showLoading(show) {
    if (show) {
      this.loadingTarget.classList.remove('d-none')
    } else {
      this.loadingTarget.classList.add('d-none')
    }
  }

  resetContainers() {
    this.imageContainerTarget.classList.add('d-none')
    this.pdfContainerTarget.classList.add('d-none')
    this.docContainerTarget.classList.add('d-none')
  }

  showImage(url) {
    this.imageTarget.src = url
    this.showLoading(false)
    this.imageContainerTarget.classList.remove('d-none')
    this.titleTarget.textContent = 'Изображение ' + this.fileName
    this.startButtonTarget.classList.add('d-none')
  }

  showPdf(url) {
    // Set the PDF src
    this.pdfTarget.src = url

    // Hide loading and show PDF container
    this.showLoading(false)
    this.pdfContainerTarget.classList.remove('d-none')

    // Set title
    this.titleTarget.textContent = 'View PDF'

    // Show start button for PDFs
    this.startButtonTarget.classList.remove('d-none')
  }

  showDoc(url) {
    // Set the doc download link
    this.docLinkTarget.href = url

    // Set the download attribute with the fileName to control download filename
    this.docLinkTarget.setAttribute('download', this.fileName)


    // Set doc name (extract from URL)
    this.docNameTarget.textContent = this.fileName

    // Hide loading and show doc container
    this.showLoading(false)
    this.docContainerTarget.classList.remove('d-none')

    // Set title
    this.titleTarget.textContent = 'Просмотр документа ' + this.fileName

    // Show start button for docs
    this.startButtonTarget.classList.remove('d-none')
  }

  addImage(editor) {
    const locale = window.locale == "ru" ? Russian : 'en_EN'
    const imageWidth = 300;
    const imageHeight = 300;

    const uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        // allowedFileTypes: ['image/*']
      },
      autoProceed: false,
      locale: locale
    })

    // Store Uppy instance as a property of this controller
    this.uppyInstance = uppy;

    uppy.use(Tus, { endpoint: window.upload_endpoint, chunkSize: 5 * 1024 * 1024 })
    uppy.use(Dashboard, {
      proudlyDisplayPoweredByUppy: false,
      autoOpenFileEditor: true,
      hideCancelButton: true
    })
    uppy.use(ImageEditor, {
      target: Dashboard,
      quality: 1,
      cropperOptions: {
        viewMode: 1,
        background: true,
        // autoCropArea: 0.8,
        responsive: true,
        // aspectRatio: 1 / 1,
        croppedCanvasOptions: {
          // widimageWidthth: imageWidth,
          // height: imageHeight
        },
      },
      actions: {
        revert: true,
        rotate: true,
        granularRotate: true,
        flip: true,
        zoomIn: true,
        zoomOut: true,
        cropSquare: false,
        cropWidescreen: false,
        cropWidescreenVertical: false,
      },
    })

    uppy.on('upload-success', (file, response) => {
      // Get the dimensions from the file's metadata after cropping
      const width = file.meta.width || imageWidth;
      const height = file.meta.height || imageHeight;

      let fileData = {
        title: file.name,
        file_orig_name: file.name,
        file_url: response.uploadURL,
        file_size: file.size,
        file_type: file.meta.type,
        width: width,
        height: height
      }
      this.createFile(fileData, editor, uppy);
      // The modal will be closed in createFile after ensuring the URL is accessible
    });

    uppy.getPlugin('Dashboard').openModal();
  }

  createFile(fileData, editor, uppy) {
    let fileDataCreate = fileData;
    delete fileDataCreate.width;
    delete fileDataCreate.height;
    const parameters = JSON.stringify({ file: fileDataCreate });
    const token = document.getElementsByName("csrf-token")[0].content;

    // Show a waiting overlay
    this.showWaitingOverlay('Ожидание загрузки...');

    fetch('/api/v1/files.json', {
      body: parameters,
      method: 'POST',
      credentials: "include",
      headers: {
        'Content-Type': 'application/json',
        "X-CSRF-Token": token
      },
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        // Check if URL is accessible before inserting
        this.checkImageUrlAccessibility(data.url)
          .then(isAccessible => {
            // Hide waiting overlay
            this.hideWaitingOverlay();

            if (isAccessible) {
              // Insert the image with the width and height from the cropped image
              // editor.insertContent(`<img src="${data.url}" width="${fileData.width}" height="${fileData.height}">`);
              console.log("Image URL is accessible:", data.url);
            } else {
              console.error("Image URL is not accessible:", data.url);
              editor.notificationManager.open({
                text: "The uploaded image URL is not accessible. Please try again.",
                type: "error"
              });
            }
            // Always close the modal, even if URL is not accessible
            if (uppy && uppy.getPlugin('Dashboard')) {
              uppy.getPlugin('Dashboard').closeModal();
            }
          })
          .catch(error => {
            // Hide waiting overlay
            this.hideWaitingOverlay();

            console.error("Error checking image URL:", error);
            // Insert image with dimensions
            editor.insertContent(`<img src="${data.url}" width="${fileData.width}" height="${fileData.height}">`);
            if (uppy && uppy.getPlugin('Dashboard')) {
              uppy.getPlugin('Dashboard').closeModal();
            }
          });
      })
      .catch(error => {
        // Hide waiting overlay
        this.hideWaitingOverlay();

        console.error("Error creating file:", error);
        if (uppy && uppy.getPlugin('Dashboard')) {
          uppy.getPlugin('Dashboard').closeModal();
        }
      });
  }

  // Method to check if an image URL is accessible with multiple retries
  checkImageUrlAccessibility(url) {
    return true
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20;
      const intervalMs = 2000;

      const checkWithRetry = () => {
        attempts++;
        console.log(`Checking image accessibility, attempt ${attempts} of ${maxAttempts}: ${url}`);

        this.updateWaitingMessage(`Ожидание загрузки... ${attempts}/${maxAttempts}`);

        const img = new Image();
        img.onload = function () {
          console.log(`Image accessible after ${attempts} attempts: ${url}`);
          resolve(true);
        };
        img.onerror = function () {
          if (attempts < maxAttempts) {
            // Try again after interval
            setTimeout(checkWithRetry, intervalMs);
          } else {
            console.error(`Image not accessible after ${maxAttempts} attempts: ${url}`);
            resolve(false);
          }
        };
        img.src = url;
      };

      checkWithRetry();

      // Set an overall timeout in case the checks get stuck
      setTimeout(() => {
        if (attempts < maxAttempts) {
          console.warn(`Overall timeout reached for image accessibility check: ${url}`);
          resolve(true); // Default to accessible after timeout
        }
      }, (maxAttempts + 5) * intervalMs);
    });
  }

  // Create an overlay to show while waiting for image accessibility check
  showWaitingOverlay(message) {
    // Remove any existing overlay first
    this.hideWaitingOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'waiting-overlay';
    overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      `;

    const messageBox = document.createElement('div');
    messageBox.id = 'waiting-message';
    messageBox.style.cssText = `
        background-color: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        text-align: center;
        max-width: 80%;
      `;

    const spinner = document.createElement('div');
    spinner.style.cssText = `
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        animation: spin 2s linear infinite;
        margin: 0 auto 15px auto;
      `;

    // Add the CSS animation for the spinner
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
    document.head.appendChild(style);

    const text = document.createElement('p');
    text.textContent = message;

    messageBox.appendChild(spinner);
    messageBox.appendChild(text);
    overlay.appendChild(messageBox);
    document.body.appendChild(overlay);
  }

  // Update the waiting message
  updateWaitingMessage(message) {
    const messageElement = document.querySelector('#waiting-message p');
    if (messageElement) {
      messageElement.textContent = message;
    }
  }

  // Remove the waiting overlay
  hideWaitingOverlay() {
    const overlay = document.getElementById('waiting-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

}
